import { useCallback, useEffect, useRef, useState } from 'react';
import { PAD_RESULT_CONFIRM_MS } from './usePadResultSubmission';

// ─────────────────────────────────────────────────────────────────────
// «SEND RESULTAT» REPORTS ON ITSELF, IN THE PORTAL'S PAD PREVIEW TOO (#557).
//
// `CourtPadCard` has said «Sender…» → «Registrert» since #216, for every
// format. On the physical pad it works, because `BanepadDeviceShell` owns a
// submission state machine and hands the card a `submitStatus`. In the portal's
// Banepad view NOTHING passed one — so `phase` was permanently `idle` and the
// button never moved, which is exactly what QA reported.
//
// THE SAME TIMING AS THE REAL PAD, from the same constant rather than a second
// number that agrees today: a confirmation that lingers 6 s on the wall and 2 s
// in the preview would be two behaviours wearing one label.
//
// WHY THIS IS NOT `usePadResultSubmission`. That hook owns a NETWORK
// conversation — retries, refusals, a device token, and the server's court view
// as the only committed truth. The portal is not in that conversation: its
// write is local state first and persistence after, so there is nothing to
// retry and no remote view to adopt. What the two share is the PHASE the button
// renders, and that is what this hook is.
//
// «SENDING» IS THEREFORE BRIEF HERE, and deliberately not padded out. The
// portal's record call returns as soon as the tournament state is updated, so
// an operator usually sees «Registrert» immediately. Faking a delay to make the
// preview look busier would be inventing a wait that is not happening.
// ─────────────────────────────────────────────────────────────────────

export interface PadSubmitPhase {
  /** Exactly the shape `CourtPadCard`/`BanepadCourtScreen` already read. */
  submitStatus: { phase: 'idle' | 'sending' | 'done'; matchId: string | null };
  /**
   * Run `record` for `matchId`, reporting the phase around it.
   *
   * Wraps rather than being called in two places, so a caller cannot report
   * «sending» and then forget to settle it — a button stuck on «Sender…» is a
   * worse failure than one that never moves, because it also goes inert.
   */
  reporting: <T>(matchId: unknown, record: () => T) => T;
}

export function usePadSubmitPhase(confirmMs: number = PAD_RESULT_CONFIRM_MS): PadSubmitPhase {
  const [state, setState] = useState<{
    phase: 'idle' | 'sending' | 'done';
    matchId: string | null;
  }>({ phase: 'idle', matchId: null });
  // The confirmation clears itself, exactly as it does on the wall-mounted pad.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const reporting = useCallback(
    <T>(matchId: unknown, record: () => T): T => {
      const id = String(matchId ?? '');
      if (!id) return record();
      if (timer.current) clearTimeout(timer.current);
      setState({ phase: 'sending', matchId: id });
      try {
        const out = record();
        setState({ phase: 'done', matchId: id });
        timer.current = setTimeout(() => setState({ phase: 'idle', matchId: null }), confirmMs);
        return out;
      } catch (e) {
        // BACK TO IDLE, NOT STUCK ON «SENDER…». The portal surfaces the error
        // its own way; what must not happen is a button that stays inert and
        // claims to be working when nothing is.
        setState({ phase: 'idle', matchId: null });
        throw e;
      }
    },
    [confirmMs],
  );

  return { submitStatus: state, reporting };
}
